/**
 * Phone number formatting utilities
 */

export interface PhoneConfig {
  number: string;
  alias?: string;
  showNumber: boolean;
}

/**
 * Format a phone number for display
 * +15551234567 → +1 (555) 123-4567
 * +442012345678 → +44 20 1234 5678
 */
export function formatPhoneNumber(phone: string): string {
  if (!phone) return "";

  // Remove all non-digit characters except leading +
  const cleaned = phone.replace(/[^\d+]/g, "");

  // US/Canada numbers: +1XXXXXXXXXX
  if (cleaned.startsWith("+1") && cleaned.length === 12) {
    const areaCode = cleaned.slice(2, 5);
    const prefix = cleaned.slice(5, 8);
    const line = cleaned.slice(8, 12);
    return `+1 (${areaCode}) ${prefix}-${line}`;
  }

  // Generic international: just add spaces for readability
  if (cleaned.startsWith("+")) {
    // Format as: +XX XXX XXX XXXX (groups of 3-4)
    const countryCode = cleaned.slice(0, cleaned.length > 11 ? 3 : 2);
    const rest = cleaned.slice(countryCode.length);

    // Split rest into groups of 3-4 digits
    const groups: string[] = [];
    for (let i = 0; i < rest.length; i += 3) {
      groups.push(rest.slice(i, Math.min(i + 3, rest.length)));
    }

    return `${countryCode} ${groups.join(" ")}`.trim();
  }

  // No country code - assume US and format accordingly
  if (cleaned.length === 10) {
    const areaCode = cleaned.slice(0, 3);
    const prefix = cleaned.slice(3, 6);
    const line = cleaned.slice(6, 10);
    return `(${areaCode}) ${prefix}-${line}`;
  }

  // Return as-is if we can't parse it
  return phone;
}

/**
 * Get display name for a phone configuration
 * Respects showNumber and alias settings
 */
export function getPhoneDisplayName(config: PhoneConfig): string {
  const formatted = formatPhoneNumber(config.number);

  // If showNumber is false and alias exists, show alias only
  if (!config.showNumber && config.alias) {
    return config.alias;
  }

  // If alias exists and showNumber is true, show both
  if (config.alias) {
    return `${config.alias} (${formatted})`;
  }

  // No alias, just formatted number
  return formatted;
}

/**
 * Validate if a string looks like a valid phone number
 */
export function isValidPhoneNumber(phone: string): boolean {
  if (!phone) return false;

  // Remove all non-digit characters except leading +
  const cleaned = phone.replace(/[^\d+]/g, "");

  // Must start with + or be 10+ digits
  if (cleaned.startsWith("+")) {
    // International: at least 8 digits after +
    return cleaned.length >= 9 && cleaned.length <= 16;
  }

  // US without country code: exactly 10 digits
  return cleaned.length === 10;
}

/**
 * Normalize a phone number to E.164 format
 * Used for routing and API calls
 */
export function normalizePhoneNumber(phone: string): string {
  if (!phone) return "";

  // Remove all non-digit characters except leading +
  const cleaned = phone.replace(/[^\d+]/g, "");

  // Already has country code
  if (cleaned.startsWith("+")) {
    return cleaned;
  }

  // Assume US/Canada if 10 digits
  if (cleaned.length === 10) {
    return `+1${cleaned}`;
  }

  // Return as-is
  return cleaned;
}
